import {
  FocusEvent,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { InputBaseProps } from '@material-ui/core/InputBase';
import InputLabel from '@material-ui/core/InputLabel';
import clsx from 'clsx';
import moment from 'moment';

import { apiDateFormat } from '../../utils';
import { InputStyled, useStyles } from './Input.styles';
import { InputMask, InputProps } from './Input.types';
import { CalendarAdornment } from './calendarAdornment/CalendarAdornment';
import { CheckAdornment } from './checkAdornment/CheckAdornment';
import { Mask } from './mask/Mask';

/**
 * @component Input
 * @description Text fields allow users to input custom text entries with a keyboard.
 * This component supports various types of text input (standard text, email, password, etc.)
 * as well as special formats like dates through masking.
 *
 * @designSystem
 * - **Variants**:
 *   - `standard` (default): Shows only a bottom border
 *   - `outlined`: Shows a visible border around the input
 *
 * - **Sizes**:
 *   - `small`: Compact size for space-constrained areas
 *   - `medium`: Default size for general forms
 *   - `large`: Expanded size for greater visibility
 *
 * - **Special Features**:
 *   - Masking: Format input as phone, date, or other structured data
 *   - Calendar: Date picker integration for date inputs
 *   - Adornments: Icons or buttons inside the input
 *
 * - **States**:
 *   - Normal: Default interactive state
 *   - Focused: When the input has keyboard focus
 *   - Error: Indicates validation errors
 *   - Disabled: Non-interactive state
 *
 * @usage
 * - Use for collecting text-based information from users
 * - Use appropriate input types for validation (email, password, etc.)
 * - Provide clear labels to indicate what information is needed
 * - Use helper text to provide additional guidance
 * - Show/hide password content when using password type
 * - Use validation and error states to guide users
 * - Consider using masked input for formatted data (phone numbers, dates)
 *
 * @accessibility
 * - Always use labels for inputs
 * - Provide descriptive placeholder text when appropriate
 * - Associate helper text and error messages using aria-describedby
 * - Ensure sufficient color contrast for all states
 * - Provide clear validation error messages
 *
 * @example
 * ```jsx
 * // Standard text input
 * <Input label="Name" placeholder="Enter your name" />
 *
 * // Email input with validation
 * <Input type="email" label="Email" error={!isValidEmail} helperText="Please enter a valid email" />
 *
 * // Password input
 * <Input type="password" label="Password" />
 *
 * // Date input with calendar
 * <Input label="Date" mask="date" dateFormat="MM/DD/YYYY" calendarAdornment />
 * ```
 */
export const Input = memo(
  forwardRef<HTMLInputElement, InputProps>(
    (
      {
        variant = 'standard',
        size = 'large',
        label,
        labelProps,
        placeholder,
        mask,
        checkAdornment,
        calendarAdornment,
        calendarAdornmentPopperProps,
        dateFormat,
        minDate,
        maxDate,
        currentDateTime,
        startAdornment,
        startAdornmentWidth: providedStartAdornmentWidth,
        emptyStateValue,
        className,
        onFocus,
        onBlur,
        onChange,
        ...props
      }: InputProps,
      ref,
    ) => {
      // Refs
      const inputRef = useRef<HTMLInputElement>(null);
      const startAdornmentRef = useRef<HTMLDivElement>(null);
      const isCalendarOpenRef = useRef<boolean>(false);

      // Destructure error to control when it's passed to Material-UI
      // Material-UI sets aria-invalid="false" when error={false}, so we only pass it when true
      const { error, ...restProps } = props;

      // States
      const [isFocused, setIsFocused] = useState<boolean>(false);
      const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
      const [measuredWidth, setMeasuredWidth] = useState<number>(0);

      // Calculate the effective width to use for styling
      const effectiveWidth = useMemo(
        () => providedStartAdornmentWidth || measuredWidth,
        [providedStartAdornmentWidth, measuredWidth],
      );

      // Create styles with the effective width
      const styles = useStyles({ startAdornmentWidth: effectiveWidth });

      const getEmptyStateValue = useCallback(() => {
        if (typeof emptyStateValue === 'function') {
          return emptyStateValue();
        }

        return emptyStateValue;
      }, [emptyStateValue]);

      const isEmptyValue = useMemo(
        () => !restProps.value || restProps.value === getEmptyStateValue(),
        [restProps.value, getEmptyStateValue],
      );

      const shouldShowRenderValue = useMemo(
        () => !!getEmptyStateValue() && isEmptyValue && !isFocused,
        [isEmptyValue, isFocused, getEmptyStateValue],
      );

      const shouldAutoShowCalendarOnFocus = useMemo(
        () => calendarAdornment && mask === 'date',
        [calendarAdornment, mask],
      );

      // Memoize labelShrink calculation to prevent unnecessary calculations
      const labelShrink = useMemo(
        () =>
          !!(
            labelProps?.shrink ||
            isFocused ||
            (restProps.value && restProps.value !== '') ||
            // internalValue ||
            restProps.value === 0 ||
            shouldShowRenderValue
          ) || undefined,
        [labelProps?.shrink, restProps.value, isFocused, shouldShowRenderValue],
      );

      const dateRestrictions = useMemo(() => {
        const minDateMoment = minDate ?? moment({ year: 1970, month: 0, day: 1 });
        const maxDateMoment = maxDate ?? moment({ year: 2100, month: 0, day: 1 });

        return {
          minDate: minDateMoment,
          maxDate: maxDateMoment,
          minDateStr: minDateMoment.format(apiDateFormat),
          maxDateStr: maxDateMoment.format(apiDateFormat),
        };
      }, [minDate, maxDate]);

      const shouldSkipMaskBlurValidation = useCallback((e: FocusEvent<HTMLInputElement>) => {
        // If the calendar is not open, do not skip validation
        if (!isCalendarOpenRef.current) {
          return false;
        }

        const relatedTarget = e.relatedTarget as HTMLElement | null;

        // Check if relatedTarget is inside the calendar popper
        // Search for popper content with the data-popper-content attribute
        const popperContentElement = document.querySelector('[data-popper-content="true"]');

        const isClickInsidePopper =
          popperContentElement && relatedTarget && popperContentElement.contains(relatedTarget);

        // Skip validation if the click was inside the calendar popper
        return Boolean(isClickInsidePopper);
      }, []);

      const maskKey = Array.isArray(mask) ? mask.join(',') : mask;

      // Memoize the InputComponent to prevent unnecessary recalculations
      const InputComponent = useMemo(() => {
        if (!mask) {
          return undefined;
        }

        return Mask(mask as InputMask, {
          dateMaskOptions: {
            currentDateTime,
            minDate: dateRestrictions.minDate,
            maxDate: dateRestrictions.maxDate,
            shouldSkipBlurValidation: mask === 'date' ? shouldSkipMaskBlurValidation : undefined,
          },
        });
      }, [
        maskKey,
        currentDateTime,
        dateRestrictions.minDateStr,
        dateRestrictions.maxDateStr,
        shouldSkipMaskBlurValidation,
      ]);

      const InputComponentToUse = useMemo(
        // The mask components type their own props; MUI hands them InputBaseComponentProps at runtime.
        () => (shouldShowRenderValue || !mask ? undefined : (InputComponent as InputBaseProps['inputComponent'])),
        [shouldShowRenderValue, mask, InputComponent],
      );

      // Calculate class name once to avoid recreating it
      const rootClassName = useMemo(
        () =>
          clsx(
            className,
            styles.root,
            variant === 'outlined' && styles.rootOutlined,
            size === 'small' && styles.rootSmall,
            size === 'medium' && styles.rootMedium,
            size === 'large' && styles.rootLarge,
            error && styles.rootError,
            restProps.disabled && styles.rootDisabled,
            isCalendarOpen && styles.rootFocused,
          ),
        [className, variant, size, error, isCalendarOpen, restProps.disabled],
      );

      const createClearEvent = useCallback(
        () =>
          ({
            target: Object.assign(document.createElement('input'), { value: '' }),
          }) as React.ChangeEvent<HTMLInputElement>,
        [],
      );

      const handleFocus = useCallback(
        (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          setIsFocused(true);

          if (shouldShowRenderValue) {
            onChange?.(createClearEvent());
          }

          onFocus?.(e);
        },
        [shouldShowRenderValue, onFocus, onChange, createClearEvent],
      );

      const handleBlur = useCallback(
        (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          setIsFocused(false);

          onBlur?.(e);
        },
        [onBlur],
      );

      // Update ref when calendar open state changes
      useEffect(() => {
        isCalendarOpenRef.current = isCalendarOpen;
      }, [isCalendarOpen]);

      // Measure startAdornment width only once on mount and when startAdornment changes
      useLayoutEffect(() => {
        if (startAdornment && !providedStartAdornmentWidth && startAdornmentRef.current) {
          const width = startAdornmentRef.current.getBoundingClientRect().width;

          if (width > 0 && width !== measuredWidth) {
            setMeasuredWidth(width);
          }
        }
      }, [startAdornment, providedStartAdornmentWidth, measuredWidth]);

      return (
        <div data-testid={restProps['data-testid']} className={rootClassName}>
          {label && (
            <InputLabel htmlFor={restProps.id || restProps.name} error={error} {...labelProps} shrink={labelShrink}>
              {label}
            </InputLabel>
          )}

          <InputStyled
            ref={ref}
            placeholder={!label ? placeholder : ''}
            id={restProps.id || restProps.name}
            {...restProps}
            {...(error && { error: true })}
            value={shouldShowRenderValue ? getEmptyStateValue() : restProps.value}
            startAdornment={
              startAdornment && (
                <div className={styles.startAdornment} ref={startAdornmentRef}>
                  {startAdornment}
                </div>
              )
            }
            endAdornment={
              checkAdornment ? (
                <CheckAdornment />
              ) : calendarAdornment ? (
                <CalendarAdornment
                  open={shouldAutoShowCalendarOnFocus && isFocused}
                  value={restProps.value}
                  type={mask === 'date-range' ? 'date-range' : 'date'}
                  dateFormat={dateFormat}
                  minDate={dateRestrictions.minDate}
                  maxDate={dateRestrictions.maxDate}
                  popperProps={calendarAdornmentPopperProps}
                  disabled={restProps.disabled}
                  className={clsx(label && styles.calendarAdornment)}
                  onChange={onChange}
                  onPopperOpenChange={setIsCalendarOpen}
                />
              ) : (
                props.endAdornment
              )
            }
            autoComplete={shouldAutoShowCalendarOnFocus ? 'off' : props.autoComplete}
            inputComponent={InputComponentToUse}
            inputProps={{
              // Filter out aria-invalid from restProps.inputProps if it's false, then add our own
              ...(restProps.inputProps && {
                ...Object.fromEntries(Object.entries(restProps.inputProps).filter(([key]) => key !== 'aria-invalid')),
              }),
              ref: inputRef,
              style: restProps.inputProps?.style,
              'aria-label': restProps['aria-label'],
              // Only set aria-invalid to true when explicitly needed, otherwise omit it completely
              // This prevents Material-UI from defaulting to aria-invalid="false"
              'aria-invalid': restProps['aria-invalid'] === true ? true : undefined,
              'aria-describedby':
                restProps['aria-describedby'] === undefined ? undefined : restProps['aria-describedby'],
              'aria-required': restProps['aria-required'] === true ? true : undefined,
              'aria-autocomplete':
                restProps['aria-autocomplete'] === undefined ? undefined : restProps['aria-autocomplete'],
              'aria-controls': restProps['aria-controls'] === undefined ? undefined : restProps['aria-controls'],
              'aria-expanded': restProps['aria-expanded'] === true ? true : undefined,
              'aria-haspopup': restProps['aria-haspopup'] === true ? true : undefined,
              'aria-activedescendant':
                restProps['aria-activedescendant'] === undefined ? undefined : restProps['aria-activedescendant'],
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={onChange}
          />
        </div>
      );
    },
  ),
);

Input.displayName = 'Input';
